import { useCallback, useEffect, useState } from 'react';
import { useAtom } from 'jotai';
import {
  api,
  type PushConfig,
  type PushPreferences,
  type PushSubscriptionRow,
} from '../lib/api';
import {
  getInstallPrompt,
  onInstallPromptChange,
  promptInstall,
  refreshPushSubscribed,
  serviceWorkerRegistrationFailed,
  vapidPublicKeyToBytes,
} from '../lib/pwa';
import {
  errorReason,
  problemKey,
  pushEnvironment,
  PushSetupError,
  readyRegistration,
  subscribeProblem,
  unsupportedReason,
  type PushProblem,
} from '../lib/pushProblem';
import { pushPreferencesAtom } from '../store';
import { t } from '../i18n';

export type PushSupport = 'checking' | 'unsupported' | 'supported';

const DEFAULT_PREFERENCES: PushPreferences = {
  messages: true,
  familyApps: true,
  calls: true,
  mutedFamilyIds: [],
  mutedConversationIds: [],
};

function pushSupported(): boolean {
  return (
    typeof window !== 'undefined' &&
    // Present but empty in some private windows.
    Boolean(navigator.serviceWorker) &&
    'PushManager' in window &&
    'Notification' in window
  );
}

/** A problem said in the person's language, the fix included (D307). */
export function problemText(problem: PushProblem, err?: unknown): string {
  return t(problemKey(problem), { reason: err ? errorReason(err) : '' });
}

export function usePush() {
  const [support, setSupport] = useState<PushSupport>('checking');
  const [config, setConfig] = useState<PushConfig | null>(null);
  const [permission, setPermission] = useState<NotificationPermission>(
    typeof Notification !== 'undefined' ? Notification.permission : 'default'
  );
  const [subscribed, setSubscribed] = useState(false);
  // Shared with the chat view and useAlerts so every mute toggle agrees.
  const [storedPreferences, setPreferences] = useAtom(pushPreferencesAtom);
  const preferences = storedPreferences ?? DEFAULT_PREFERENCES;
  const [subscriptions, setSubscriptions] = useState<PushSubscriptionRow[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Why it's unsupported here, so Settings can name the fix (D307).
  const [problem, setProblem] = useState<PushProblem | null>(null);

  const refresh = useCallback(async () => {
    const reason = pushSupported()
      ? serviceWorkerRegistrationFailed()
        ? 'swFailed'
        : null
      : (unsupportedReason(pushEnvironment()) ?? 'unsupported');
    if (reason) {
      setProblem(reason);
      setSupport('unsupported');
      return;
    }
    setProblem(null);
    setSupport('supported');
    setPermission(Notification.permission);

    try {
      const [configRes, prefRes, subRes] = await Promise.all([
        api.getPushConfig(),
        api.getPushPreferences(),
        api.getPushSubscriptions(),
      ]);
      if (configRes.success) setConfig(configRes.data);
      if (prefRes.success) setPreferences(prefRes.data);
      if (subRes.success) setSubscriptions(subRes.data);

      const registration = await readyRegistration();
      const existing = await registration.pushManager.getSubscription();
      setSubscribed(Boolean(existing));
    } catch (err) {
      // The service worker never got ready: nothing here can work (D308).
      if (err instanceof PushSetupError) {
        setProblem(err.problem);
        setSupport('unsupported');
      }
      // Otherwise settings still render if the server is unreachable.
    }
  }, [setPreferences]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const enable = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      if (!pushSupported()) {
        throw new Error(
          problemText(unsupportedReason(pushEnvironment()) ?? 'unsupported')
        );
      }
      if (!config?.enabled || !config.publicKey) {
        throw new Error(t('account.push.notConfigured'));
      }

      const result = await Notification.requestPermission();
      setPermission(result);
      // Denied names where to allow them; a private window denies by itself.
      if (result === 'denied') throw new Error(problemText('blocked'));
      if (result !== 'granted') {
        throw new Error(t('account.push.notGranted'));
      }

      let subscription: PushSubscription | null;
      try {
        const registration = await readyRegistration();
        subscription = await registration.pushManager.getSubscription();
        if (!subscription) {
          subscription = await registration.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: vapidPublicKeyToBytes(config.publicKey),
          });
        }
      } catch (err) {
        // Said, not swallowed: Brave's push setting, a push service that
        // didn't answer, a service worker that never started (D308).
        throw new Error(
          problemText(subscribeProblem(err, pushEnvironment()), err)
        );
      }

      const json = subscription.toJSON();
      if (!json.endpoint || !json.keys?.p256dh || !json.keys?.auth) {
        throw new Error(t('account.push.incomplete'));
      }

      await api.subscribePush({
        endpoint: json.endpoint,
        keys: { p256dh: json.keys.p256dh, auth: json.keys.auth },
        userAgent: navigator.userAgent.slice(0, 255),
      });

      setSubscribed(true);
      void refreshPushSubscribed();
      await refresh();
    } catch (err) {
      setError(
        err instanceof Error ? err.message : t('account.push.enableFailed')
      );
    } finally {
      setBusy(false);
    }
  }, [config, refresh]);

  const disable = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      if (!pushSupported()) return;
      const registration = await readyRegistration();
      const subscription = await registration.pushManager.getSubscription();
      if (subscription) {
        await api.unsubscribePush(subscription.endpoint).catch(() => undefined);
        await subscription.unsubscribe();
      }
      setSubscribed(false);
      void refreshPushSubscribed();
      await refresh();
    } catch (err) {
      setError(
        err instanceof Error ? err.message : t('account.push.disableFailed')
      );
    } finally {
      setBusy(false);
    }
  }, [refresh]);

  const updatePreferences = useCallback(
    async (update: Partial<PushPreferences>) => {
      const previous = preferences;
      setPreferences({ ...preferences, ...update });
      try {
        const response = await api.updatePushPreferences(update);
        if (response.success) setPreferences(response.data);
      } catch {
        setPreferences(previous);
        setError(t('account.push.saveFailed'));
      }
    },
    [preferences, setPreferences]
  );

  const sendTest = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      await api.sendTestPush();
    } catch (err) {
      setError(
        err instanceof Error ? err.message : t('account.push.testFailed')
      );
    } finally {
      setBusy(false);
    }
  }, []);

  // The push service can rotate an endpoint; the service worker tells us and
  // we re-subscribe with the current VAPID key.
  useEffect(() => {
    if (typeof navigator === 'undefined' || !navigator.serviceWorker) {
      return;
    }
    const onMessage = (event: MessageEvent) => {
      if (event.data?.type === 'push:resubscribe') {
        void enable();
      }
    };
    navigator.serviceWorker.addEventListener('message', onMessage);
    return () => {
      navigator.serviceWorker.removeEventListener('message', onMessage);
    };
  }, [enable]);

  return {
    support,
    problem,
    config,
    permission,
    subscribed,
    preferences,
    subscriptions,
    busy,
    error,
    enable,
    disable,
    refresh,
    updatePreferences,
    sendTest,
  };
}

export function useInstallPrompt() {
  const [available, setAvailable] = useState(false);

  useEffect(() => {
    // The event is captured at module load; check whether one is waiting.
    setAvailable(Boolean(getInstallPrompt()));
    return onInstallPromptChange((event) => setAvailable(Boolean(event)));
  }, []);

  const prompt = useCallback(() => promptInstall(), []);

  return { available, prompt };
}
